/**
 * Performance Monitoring Utilities
 * Following constitution requirements for ArbiWin platform
 *
 * This file provides performance monitoring, measurement,
 * and optimization utilities for the application.
 */

import { browserHelpers } from './helpers';

/**
 * Performance Metrics Collection
 */
export const performanceMetrics = {
  /**
   * Collect Web Vitals metrics
   * @param {Function} callback - Callback to receive metrics
   * @returns {Function} Cleanup function
   */
  collectWebVitals: function(callback) {
    if (!browserHelpers.isBrowser() || !callback || typeof callback !== 'function') {
      return function() {}; // No-op cleanup
    }

    const metrics = {
      CLS: null,
      FID: null,
      FCP: null,
      LCP: null,
      TTFB: null
    };

    // Collect metrics when available
    const observer = new PerformanceObserver(function(list) {
      list.getEntries().forEach(function(entry) {
        switch (entry.entryType) {
          case 'layout-shift':
            if (!entry.hadRecentInput) {
              metrics.CLS = (metrics.CLS || 0) + entry.value;
            }
            break;
          case 'first-input':
            metrics.FID = entry.processingStart - entry.startTime;
            break;
          case 'paint':
            if (entry.name === 'first-contentful-paint') {
              metrics.FCP = entry.startTime;
            }
            break;
          case 'largest-contentful-paint':
            metrics.LCP = entry.startTime;
            break;
          case 'navigation':
            metrics.TTFB = entry.responseStart - entry.requestStart;
            break;
        }

        callback(Object.assign({}, metrics));
      });
    });

    // Observe different entry types
    try {
      observer.observe({ entryTypes: ['layout-shift', 'first-input', 'paint', 'largest-contentful-paint', 'navigation'] });
    } catch (error) {
      console.warn('Performance Observer not fully supported:', error);
    }

    // Return cleanup function
    return function() {
      observer.disconnect();
    };
  },

  /**
   * Measure component render time
   * @param {string} componentName - Component name
   * @param {Function} renderFunction - Render function to measure
   * @returns {any} Render function result
   */
  measureComponentRender: function(componentName, renderFunction) {
    if (!renderFunction || typeof renderFunction !== 'function') {
      return null;
    }

    const startMark = 'render-start-' + componentName;
    const endMark = 'render-end-' + componentName;
    const measureName = 'render-' + componentName;

    // Start measurement
    if (browserHelpers.isBrowser() && performance.mark) {
      performance.mark(startMark);
    }

    const result = renderFunction();

    // End measurement
    if (browserHelpers.isBrowser() && performance.mark && performance.measure) {
      performance.mark(endMark);
      try {
        performance.measure(measureName, startMark, endMark);
      } catch (error) {
        console.warn('Performance measurement failed for ' + componentName + ':', error);
      }
    }

    return result;
  },

  /**
   * Monitor API call performance
   * @param {string} endpoint - API endpoint
   * @param {Promise} apiCall - API call promise
   * @returns {Promise} API call result with timing
   */
  monitorApiCall: function(endpoint, apiCall) {
    const startTime = performance.now();

    return apiCall
      .then(function(result) {
        const endTime = performance.now();
        const duration = endTime - startTime;

        // Log performance in development
        if (process.env.NODE_ENV === 'development') {
          console.log('🚀 API Performance:', {
            endpoint: endpoint,
            duration: Math.round(duration) + 'ms',
            status: 'success'
          });
        }

        // Dispatch performance event
        if (browserHelpers.isBrowser()) {
          window.dispatchEvent(new CustomEvent('arbiwin:apiPerformance', {
            detail: {
              endpoint: endpoint,
              duration: duration,
              status: 'success',
              timestamp: new Date().toISOString()
            }
          }));
        }

        return result;
      })
      .catch(function(error) {
        const endTime = performance.now();
        const duration = endTime - startTime;

        // Log performance in development
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ API Performance:', {
            endpoint: endpoint,
            duration: Math.round(duration) + 'ms',
            status: 'error',
            error: error.message
          });
        }

        // Dispatch performance event
        if (browserHelpers.isBrowser()) {
          window.dispatchEvent(new CustomEvent('arbiwin:apiPerformance', {
            detail: {
              endpoint: endpoint,
              duration: duration,
              status: 'error',
              error: error.message,
              timestamp: new Date().toISOString()
            }
          }));
        }

        throw error;
      });
  }
};

/**
 * Memory Management Utilities
 */
export const memoryHelpers = {
  /**
   * Get memory usage information
   * @returns {Object} Memory usage stats
   */
  getMemoryUsage: function() {
    if (!browserHelpers.isBrowser() || !performance.memory) {
      return {
        supported: false,
        used: 0,
        total: 0,
        limit: 0
      };
    }

    return {
      supported: true,
      used: performance.memory.usedJSHeapSize,
      total: performance.memory.totalJSHeapSize,
      limit: performance.memory.jsHeapSizeLimit
    };
  },

  /**
   * Monitor memory usage and warn if high
   * @param {number} threshold - Warning threshold (0-1)
   * @param {Function} callback - Callback for high memory warning
   * @returns {Function} Cleanup function
   */
  monitorMemoryUsage: function(threshold, callback) {
    threshold = threshold || 0.8;

    if (!browserHelpers.isBrowser() || !performance.memory || !callback) {
      return function() {}; // No-op cleanup
    }

    const intervalId = setInterval(function() {
      const usage = memoryHelpers.getMemoryUsage();
      const usageRatio = usage.used / usage.limit;

      if (usageRatio > threshold) {
        callback({
          usage: usage,
          ratio: usageRatio,
          threshold: threshold,
          timestamp: new Date().toISOString()
        });
      }
    }, 10000); // Check every 10 seconds

    return function() {
      clearInterval(intervalId);
    };
  },

  /**
   * Force garbage collection (if available)
   * @returns {boolean} True if GC was triggered
   */
  forceGarbageCollection: function() {
    if (browserHelpers.isBrowser() && window.gc && typeof window.gc === 'function') {
      try {
        window.gc();
        return true;
      } catch (error) {
        console.warn('Garbage collection failed:', error);
        return false;
      }
    }

    return false;
  }
};

/**
 * Performance Optimization Utilities
 */
export const optimizationHelpers = {
  /**
   * Create memoized function
   * @param {Function} func - Function to memoize
   * @param {Function} keyGenerator - Optional key generator function
   * @returns {Function} Memoized function
   */
  memoize: function(func, keyGenerator) {
    if (!func || typeof func !== 'function') {
      return func;
    }

    const cache = {};

    return function() {
      let key;

      if (keyGenerator && typeof keyGenerator === 'function') {
        key = keyGenerator.apply(this, arguments);
      } else {
        key = JSON.stringify(arguments);
      }

      if (cache.hasOwnProperty(key)) {
        return cache[key];
      }

      const result = func.apply(this, arguments);
      cache[key] = result;

      return result;
    };
  },

  /**
   * Create batch processor for efficient bulk operations
   * @param {Function} processor - Batch processing function
   * @param {number} batchSize - Size of each batch
   * @param {number} delay - Delay between batches
   * @returns {Function} Batch processor function
   */
  createBatchProcessor: function(processor, batchSize, delay) {
    batchSize = batchSize || 10;
    delay = delay || 0;

    return function(items) {
      if (!Array.isArray(items)) {
        return Promise.resolve([]);
      }

      const batches = [];
      for (let i = 0; i < items.length; i += batchSize) {
        batches.push(items.slice(i, i + batchSize));
      }

      let results = [];
      let currentBatch = 0;

      return new Promise(function(resolve, reject) {
        function processBatch() {
          if (currentBatch >= batches.length) {
            resolve(results);
            return;
          }

          const batch = batches[currentBatch];
          currentBatch++;

          Promise.resolve(processor(batch))
            .then(function(batchResults) {
              if (Array.isArray(batchResults)) {
                results = results.concat(batchResults);
              } else {
                results.push(batchResults);
              }

              if (delay > 0) {
                setTimeout(processBatch, delay);
              } else {
                processBatch();
              }
            })
            .catch(reject);
        }

        processBatch();
      });
    };
  },

  /**
   * Create virtual scrolling helper
   * @param {Array} items - Items to virtualize
   * @param {number} itemHeight - Height of each item
   * @param {number} containerHeight - Height of container
   * @param {number} scrollTop - Current scroll position
   * @returns {Object} Virtual scrolling data
   */
  calculateVirtualScrolling: function(items, itemHeight, containerHeight, scrollTop) {
    if (!Array.isArray(items) || items.length === 0) {
      return {
        visibleItems: [],
        startIndex: 0,
        endIndex: 0,
        offsetY: 0,
        totalHeight: 0
      };
    }

    const totalHeight = items.length * itemHeight;
    let startIndex = Math.floor(scrollTop / itemHeight);
    const visibleCount = Math.ceil(containerHeight / itemHeight);
    let endIndex = Math.min(startIndex + visibleCount + 1, items.length);

    // Add buffer for smooth scrolling
    const bufferSize = Math.ceil(visibleCount * 0.5);
    startIndex = Math.max(0, startIndex - bufferSize);
    endIndex = Math.min(items.length, endIndex + bufferSize);

    const visibleItems = items.slice(startIndex, endIndex);
    const offsetY = startIndex * itemHeight;

    return {
      visibleItems: visibleItems,
      startIndex: startIndex,
      endIndex: endIndex,
      offsetY: offsetY,
      totalHeight: totalHeight
    };
  },

  /**
   * Lazy load component
   * @param {Function} importFunc - Dynamic import function
   * @param {Object} options - Loading options
   * @returns {Object} Lazy component with loading state
   */
  lazyLoadComponent: function(importFunc, options) {
    options = options || {};

    // Note: React.lazy would need React import, simplified for now
    return {
      importFunc: importFunc,
      fallback: options.fallback || 'Loading...'
    };
  },

  /**
   * Create intersection observer for lazy loading
   * @param {Function} callback - Callback when element enters viewport
   * @param {Object} options - Observer options
   * @returns {IntersectionObserver} Observer instance
   */
  createIntersectionObserver: function(callback, options) {
    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      return null;
    }

    options = Object.assign({
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    }, options);

    return new IntersectionObserver(callback, options);
  },

  /**
   * Optimize image loading
   * @param {HTMLImageElement} img - Image element
   * @param {string} src - Image source
   * @param {string} placeholder - Placeholder source
   * @returns {Promise} Promise that resolves when image loads
   */
  optimizeImageLoading: function(img, src, placeholder) {
    if (!img || !src) {
      return Promise.reject(new Error('Invalid image or source'));
    }

    return new Promise(function(resolve, reject) {
      // Set placeholder if provided
      if (placeholder) {
        img.src = placeholder;
      }

      // Create new image for preloading
      const preloadImg = new Image();

      preloadImg.onload = function() {
        img.src = src;
        img.classList.add('loaded');
        resolve(img);
      };

      preloadImg.onerror = function() {
        reject(new Error('Failed to load image: ' + src));
      };

      preloadImg.src = src;
    });
  }
};

/**
 * Resource Loading Utilities
 */
export const resourceHelpers = {
  /**
   * Preload image
   * @param {string} src - Image source URL
   * @returns {Promise} Promise that resolves when image loads
   */
  preloadImage: function(src) {
    return new Promise(function(resolve, reject) {
      const img = new Image();
      img.onload = function() { resolve(img); };
      img.onerror = function() { reject(new Error('Failed to load image: ' + src)); };
      img.src = src;
    });
  },

  /**
   * Load script dynamically
   * @param {string} src - Script source URL
   * @param {Object} options - Loading options
   * @returns {Promise} Promise that resolves when script loads
   */
  loadScript: function(src, options) {
    options = options || {};

    return new Promise(function(resolve, reject) {
      if (!browserHelpers.isBrowser()) {
        reject(new Error('Not in browser environment'));
        return;
      }

      // Check if script already loaded
      const existingScript = document.querySelector('script[src="' + src + '"]');
      if (existingScript) {
        resolve(existingScript);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = options.async !== false;
      script.defer = options.defer === true;

      script.onload = function() { resolve(script); };
      script.onerror = function() { reject(new Error('Failed to load script: ' + src)); };

      document.head.appendChild(script);
    });
  },

  /**
   * Check if resource is cached
   * @param {string} url - Resource URL
   * @returns {Promise} Promise with cache status
   */
  checkResourceCache: function(url) {
    if (!browserHelpers.isBrowser() || !navigator.serviceWorker) {
      return Promise.resolve({ cached: false, supported: false });
    }

    return caches.match(url)
      .then(function(response) {
        return {
          cached: !!response,
          supported: true,
          response: response
        };
      })
      .catch(function(error) {
        return {
          cached: false,
          supported: true,
          error: error.message
        };
      });
  }
};

/**
 * Performance Monitoring Class
 */
export function PerformanceMonitor() {
  this.metrics = {};
  this.observers = [];
  this.isMonitoring = false;
}

PerformanceMonitor.prototype = {
  /**
   * Start performance monitoring
   * @param {Object} options - Monitoring options
   */
  start: function(options) {
    if (this.isMonitoring) return;

    options = options || {};
    this.isMonitoring = true;

    // Monitor Web Vitals
    if (options.webVitals !== false) {
      const webVitalsCleanup = performanceMetrics.collectWebVitals(function(metrics) {
        this.updateMetrics('webVitals', metrics);
      }.bind(this));

      this.observers.push(webVitalsCleanup);
    }

    // Monitor memory usage
    if (options.memory !== false) {
      const memoryCleanup = this.monitorMemory();
      this.observers.push(memoryCleanup);
    }

    // Monitor resource loading
    if (options.resources !== false) {
      const resourceCleanup = this.monitorResources();
      this.observers.push(resourceCleanup);
    }

    console.log('📊 Performance monitoring started');
  },

  /**
   * Stop performance monitoring
   */
  stop: function() {
    if (!this.isMonitoring) return;

    this.isMonitoring = false;

    // Clean up all observers
    this.observers.forEach(function(cleanup) {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    });

    this.observers = [];
    console.log('📊 Performance monitoring stopped');
  },

  /**
   * Update metrics
   * @param {string} category - Metrics category
   * @param {Object} data - Metrics data
   */
  updateMetrics: function(category, data) {
    this.metrics[category] = Object.assign({}, this.metrics[category], data, {
      timestamp: new Date().toISOString()
    });

    // Dispatch metrics update event
    if (browserHelpers.isBrowser()) {
      window.dispatchEvent(new CustomEvent('arbiwin:performanceUpdate', {
        detail: {
          category: category,
          metrics: this.metrics[category]
        }
      }));
    }
  },

  /**
   * Get current metrics
   * @param {string} category - Optional category filter
   * @returns {Object} Performance metrics
   */
  getMetrics: function(category) {
    if (category) {
      return this.metrics[category] || {};
    }

    return Object.assign({}, this.metrics);
  },

  /**
   * Monitor memory usage
   * @returns {Function} Cleanup function
   */
  monitorMemory: function() {
    const self = this;

    const intervalId = setInterval(function() {
      if (!browserHelpers.isBrowser() || !performance.memory) return;

      const memoryInfo = {
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit,
        usageRatio: performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit
      };

      self.updateMetrics('memory', memoryInfo);

      // Warn if memory usage is high
      if (memoryInfo.usageRatio > 0.8) {
        console.warn('⚠️ High memory usage detected:', memoryInfo.usageRatio.toFixed(2));
      }
    }, 5000); // Check every 5 seconds

    return function() {
      clearInterval(intervalId);
    };
  },

  /**
   * Monitor resource loading
   * @returns {Function} Cleanup function
   */
  monitorResources: function() {
    if (!browserHelpers.isBrowser()) {
      return function() {};
    }

    const self = this;
    const observer = new PerformanceObserver(function(list) {
      const resources = list.getEntries().map(function(entry) {
        return {
          name: entry.name,
          type: entry.initiatorType,
          duration: entry.duration,
          size: entry.transferSize || 0,
          startTime: entry.startTime
        };
      });

      self.updateMetrics('resources', {
        recent: resources,
        totalCount: (self.metrics.resources && self.metrics.resources.totalCount || 0) + resources.length
      });
    });

    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch (error) {
      console.warn('Resource monitoring not supported:', error);
    }

    return function() {
      observer.disconnect();
    };
  }
};



/**
 * Performance Reporting
 */
export const performanceReporting = {
  /**
   * Generate performance report
   * @param {Object} metrics - Performance metrics
   * @returns {Object} Performance report
   */
  generateReport: function(metrics) {
    if (!metrics || typeof metrics !== 'object') {
      return {
        timestamp: new Date().toISOString(),
        status: 'no-data',
        summary: 'No performance metrics available'
      };
    }

    const report = {
      timestamp: new Date().toISOString(),
      status: 'success',
      summary: {},
      details: metrics
    };

    // Analyze Web Vitals
    if (metrics.webVitals) {
      const vitals = metrics.webVitals;
      report.summary.webVitals = {
        CLS: vitals.CLS ? (vitals.CLS < 0.1 ? 'good' : vitals.CLS < 0.25 ? 'needs-improvement' : 'poor') : 'unknown',
        FID: vitals.FID ? (vitals.FID < 100 ? 'good' : vitals.FID < 300 ? 'needs-improvement' : 'poor') : 'unknown',
        LCP: vitals.LCP ? (vitals.LCP < 2500 ? 'good' : vitals.LCP < 4000 ? 'needs-improvement' : 'poor') : 'unknown'
      };
    }

    // Analyze memory usage
    if (metrics.memory) {
      const memory = metrics.memory;
      report.summary.memory = {
        status: memory.usageRatio < 0.6 ? 'good' : memory.usageRatio < 0.8 ? 'warning' : 'critical',
        usagePercentage: Math.round(memory.usageRatio * 100)
      };
    }

    return report;
  },

  /**
   * Log performance summary
   * @param {Object} metrics - Performance metrics
   */
  logSummary: function(metrics) {
    if (process.env.NODE_ENV !== 'development') return;

    const report = this.generateReport(metrics);

    console.group('📊 ArbiWin Performance Summary');
    console.log('Timestamp:', report.timestamp);

    if (report.summary.webVitals) {
      console.log('Web Vitals:', report.summary.webVitals);
    }

    if (report.summary.memory) {
      console.log('Memory Usage:', report.summary.memory);
    }

    console.groupEnd();
  }
};

/**
 * Global performance monitor instance
 */
export const globalPerformanceMonitor = new PerformanceMonitor();

/**
 * Initialize performance monitoring
 * @param {Object} options - Monitoring options
 * @returns {Object} Initialization result
 */
export function initializePerformanceMonitoring(options) {
  options = options || {};

  if (process.env.REACT_APP_PERFORMANCE_MONITORING === 'false') {
    return {
      enabled: false,
      reason: 'Disabled by environment variable'
    };
  }

  if (process.env.NODE_ENV === 'production' && !options.forceInProduction) {
    return {
      enabled: false,
      reason: 'Disabled in production (use forceInProduction option to enable)'
    };
  }

  globalPerformanceMonitor.start(options);

  return {
    enabled: true,
    monitor: globalPerformanceMonitor,
    timestamp: new Date().toISOString()
  };
}

/**
 * Export all performance utilities
 */
export default {
  metrics: performanceMetrics,
  memory: memoryHelpers,
  optimization: optimizationHelpers,
  reporting: performanceReporting,
  resources: resourceHelpers,
  monitor: PerformanceMonitor,
  globalMonitor: globalPerformanceMonitor,
  initialize: initializePerformanceMonitoring
};
